/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { useState, useEffect } from 'react';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { useParticipants } from '../../../../hooks/videoProvider/useParticipants';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { isEqual, compact, reject } from '@utils/lodash';

export interface UseParticipantTracks {
  isLoading: boolean;
  pinnedParticipants: VideoProvider.IParticipant[];
  nonPinnedParticipants: VideoProvider.IParticipant[];
}

export const useParticipantTracks = (): UseParticipantTracks => {
  const { room } = useVideoContext();
  const participants = useParticipants();
  const { signingRoom } = useRootSelector(state => state);
  const { users } = signingRoom;

  const [isLoading, setIsLoading] = useState(true);
  const [pinnedParticipants, setPinnedParticipants] = useState<VideoProvider.IParticipant[]>([]);
  const [nonPinnedParticipants, setNonPinnedParticipants] = useState<VideoProvider.IParticipant[]>([]);

  useEffect(() => {
    // group participants into pinned participant and non-pinned participant
    // local participant and notary participant will be pinned because they should always be visible to the participant
    // other participants will not be pinned, only one non-pinned participant will be shown on mobile view
    if (users?.inVideoCall.length > 0 && participants.length > 0) {
      const notaryParticipantSession = users?.inVideoCall.find(user => user.isNotary);
      const notaryParticipant = participants.find(participant => participant.identity === notaryParticipantSession?.id);
      const notNotaryParticipants = reject(participants, { identity: notaryParticipantSession?.id });
      const localAndNotaryParticipants = compact([room?.localParticipant, notaryParticipant]);
      if (!isEqual(localAndNotaryParticipants, pinnedParticipants)) {
        setPinnedParticipants(localAndNotaryParticipants);
      }
      if (!isEqual(notNotaryParticipants, nonPinnedParticipants)) {
        setNonPinnedParticipants(notNotaryParticipants);
      }
      setIsLoading(false);
    }
  }, [users, participants, nonPinnedParticipants, pinnedParticipants, room?.localParticipant]);

  return { isLoading, pinnedParticipants, nonPinnedParticipants };
};
